import {SagaIterator} from 'redux-saga';
import {call, cancel, select} from 'redux-saga/effects';

import {AddressType} from 'desktop/types/addressType';

import {formatPhoneNumber} from 'libs/getDeliveryFormStructure/helpers/helpers';
import {getGeolocationByAkamai} from 'libs/utils/utils';
import {StatesItemType} from 'types/locationType';

import {ActionType} from 'types/reducers';

import {getShippingMethodsSaga} from '../../deliveryStep/getShippingMethodsSaga/getShippingMethodsSaga';
import {getStatesForCountry} from '../../deliveryStep/getStatesForCountry/getStatesForCountry';
import {updateAddress} from '../updateAddressesSaga/updateAddressesSaga';
import {
    targets,
    updateOrderReviewShippingInfoSaga,
} from '../updateOrderReviewShippingInfoSaga/updateOrderReviewShippingInfoSaga';

export type UpdateDataType = {
    fieldName: string;
    value: string;
    target: string;
    useThisAddressForBothAddresses: boolean;
};

export type UpdateFieldsValueSagaType = {
    fieldName: string;
    useThisAddressForBothAddresses: boolean;
    updatedForm: AddressType;
    target: string;
};

/**
 * Prepare field and return actual data
 * @param {string} fieldName - Received current fieldName
 * @param {string} value - Received current value
 * @param {AddressType} address - Current address
 * @returns {DeliveryStepFormType} - Prepared values for fields
 */
export function* prepareFieldValue({
    address,
    fieldName,
    value,
}: {
    fieldName: string;
    value: string;
    address: AddressType;
}) {
    const preparedField = {[fieldName]: {error: false, success: false, value}};

    if (fieldName === 'phone' && getGeolocationByAkamai().includes('US')) {
        preparedField[fieldName] = {
            ...preparedField[fieldName],
            value: formatPhoneNumber(value),
        };
    }

    if (fieldName === 'state') {
        const states: StatesItemType[] = yield call(getStatesForCountry, address.country.value);
        preparedField.regionID = {
            error: false,
            success: false,
            value: states?.find(state => state?.label === value)?.id || '',
        };
    }
    return preparedField;
}

/**
 * Perform modifying of country and state fields depends on received value and returns updated fields
 * @param {string} value - Received current country value
 * @returns {DeliveryStepFormType} - Updated values for country and state fields
 */
export function* handleCountryValue(value: string) {
    const states: StatesItemType[] = yield call(getStatesForCountry, value);
    if (states.length) {
        return {
            country: {error: false, success: false, value},
            state: {error: false, success: false, value: '0'},
        };
    } else if (value) {
        return {
            country: {error: false, success: false, value},
            state: {error: false, success: false, value: ''},
        };
    }
    return {
        country: {error: false, success: false, value: 'US'},
        state: {error: false, success: false, value: '0'},
    };
}

/**
 * Updates fields value with passed data
 * @param {string} fieldName - fieldName
 * @param {AddressType} updatedForm - address form
 * @param {boolean} useThisAddressForBothAddresses - bool
 * @param {string} target - form name
 */
export function* updateFieldsValueSaga({
    fieldName,
    target,
    updatedForm,
    useThisAddressForBothAddresses,
}: UpdateFieldsValueSagaType): SagaIterator {
    if (fieldName === 'country') {
        if (target === 'shippingAddress') {
            yield call(getShippingMethodsSaga, {country: updatedForm.country.value});
        } else {
            yield call(getShippingMethodsSaga);
        }
        yield call(updateOrderReviewShippingInfoSaga);
    }

    yield call(updateAddress, updatedForm, useThisAddressForBothAddresses, target);
}

/**
 * Updates "value" field of certain field and then updates whole form
 * @param {ActionType<UpdateDataType>} action - Result of update
 */
export function* updateFieldValueSaga({
    payload: {fieldName, target, useThisAddressForBothAddresses, value},
}: ActionType<UpdateDataType>): SagaIterator {
    const address = yield select(targets[target].selector);

    let preparedField;

    if (address[fieldName].value === value) yield cancel();

    if (fieldName === 'country') {
        preparedField = yield call(handleCountryValue, value);
    } else {
        preparedField = yield call(prepareFieldValue, {address, fieldName, value});
    }

    yield call(updateFieldsValueSaga, {
        fieldName,
        target,
        updatedForm: {...address, ...preparedField},
        useThisAddressForBothAddresses,
    });
}
